import React, { useState, useEffect } from 'react'
import { useTheme } from 'src/context/ThemeContext'

// Local interface for FilterPanel - only includes API-supported filters
interface FilterPanelFilters {
  search: string
  priceRange: { min: number | null; max: number | null }
  salesRange: { min: number | null; max: number | null }
  // Note: stockRange filtering removed - not supported by API v2
}

interface FilterPanelProps {
  filters: FilterPanelFilters
  onFiltersChange: (filters: Partial<FilterPanelFilters>) => void
  'data-testid'?: string
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  'data-testid': dataTestId,
}) => {
  const { theme } = useTheme()
  const [activeDropdown, setActiveDropdown] = useState<'price' | 'sales' | null>(null)
  const [priceMin, setPriceMin] = useState<string>('')
  const [priceMax, setPriceMax] = useState<string>('')
  const [salesMin, setSalesMin] = useState<string>('')
  const [salesMax, setSalesMax] = useState<string>('')

  // Sync local state with props when filters change
  useEffect(() => {
    setPriceMin(filters.priceRange.min?.toString() || '')
    setPriceMax(filters.priceRange.max?.toString() || '')
  }, [filters.priceRange])

  useEffect(() => {
    setSalesMin(filters.salesRange.min?.toString() || '')
    setSalesMax(filters.salesRange.max?.toString() || '')
  }, [filters.salesRange])

  const handlePriceApply = () => {
    onFiltersChange({
      priceRange: {
        min: priceMin ? Number(priceMin) : null,
        max: priceMax ? Number(priceMax) : null,
      }
    })
    setActiveDropdown(null)
  }

  const handlePriceCancel = () => {
    // Reset to current prop values instead of clearing
    setPriceMin(filters.priceRange.min?.toString() || '')
    setPriceMax(filters.priceRange.max?.toString() || '')
    setActiveDropdown(null)
  }

  const handleSalesApply = () => {
    onFiltersChange({
      salesRange: {
        min: salesMin ? Number(salesMin) : null,
        max: salesMax ? Number(salesMax) : null,
      }
    })
    setActiveDropdown(null)
  }

  const handleSalesCancel = () => {
    // Reset to current prop values instead of clearing
    setSalesMin(filters.salesRange.min?.toString() || '')
    setSalesMax(filters.salesRange.max?.toString() || '')
    setActiveDropdown(null)
  }

  // Helper functions to format filter values for button text
  const formatPriceRange = (priceRange: { min: number | null; max: number | null }): string => {
    if (priceRange.min === null && priceRange.max === null) {
      return ''
    }
    const min = priceRange.min ? `Rp ${priceRange.min.toLocaleString()}` : 'Rp 0'
    const max = priceRange.max ? `Rp ${priceRange.max.toLocaleString()}` : '∞'
    return `: ${min} - ${max}`
  }

  const formatSalesRange = (salesRange: { min: number | null; max: number | null }): string => {
    if (salesRange.min === null && salesRange.max === null) {
      return ''
    }
    const min = salesRange.min ? `Rp ${salesRange.min.toLocaleString()}` : 'Rp 0'
    const max = salesRange.max ? `Rp ${salesRange.max.toLocaleString()}` : '∞'
    return `: ${min} - ${max}`
  }

  // Check if filters have values applied
  const hasPriceFilter = filters.priceRange.min !== null || filters.priceRange.max !== null
  const hasSalesFilter = filters.salesRange.min !== null || filters.salesRange.max !== null

  // Handle button clicks - clear filter if it has value, otherwise toggle dropdown
  const handlePriceButtonClick = () => {
    if (hasPriceFilter && activeDropdown !== 'price') {
      // Clear the price filter
      onFiltersChange({
        priceRange: { min: null, max: null }
      })
    } else {
      // Toggle dropdown
      setActiveDropdown(activeDropdown === 'price' ? null : 'price')
    }
  }

  const handleSalesButtonClick = () => {
    if (hasSalesFilter && activeDropdown !== 'sales') {
      // Clear the sales filter
      onFiltersChange({
        salesRange: { min: null, max: null }
      })
    } else {
      // Toggle dropdown
      setActiveDropdown(activeDropdown === 'sales' ? null : 'sales')
    }
  }

  return (
    <div className="tw-flex tw-flex-1 tw-items-center tw-gap-3" data-testid={dataTestId}>
      {/* Price Filter */}
      <div className="tw-relative">
        <button
          onClick={handlePriceButtonClick}
          data-testid="price-filter"
          className={`tw-flex tw-h-9 tw-shrink-0 tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-4 tw-py-2 tw-transition-colors ${
            activeDropdown === 'price' || hasPriceFilter
              ? 'tw-border-primary tw-bg-primary/10'
              : 'tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50'
          }`}
        >
          <p className={`tw-text-sm tw-font-medium tw-leading-normal ${
            activeDropdown === 'price' || hasPriceFilter ? 'tw-text-primary' : 'tw-text-gray-800'
          }`}>
            Price{formatPriceRange(filters.priceRange)}
          </p>
          <span className={`material-symbols-outlined ${
            activeDropdown === 'price' || hasPriceFilter ? 'tw-text-primary' : 'tw-text-gray-500'
          }`}>
            expand_more
          </span>
        </button>
        {activeDropdown === 'price' && (
          <div className={`tw-absolute tw-left-0 tw-mt-2 tw-w-64 tw-origin-top-left tw-rounded-lg tw-border tw-p-4 tw-shadow-lg tw-z-[9999] ${
            theme === 'light' 
              ? 'tw-border-gray-200 tw-bg-white' 
              : 'tw-border-slate-600 tw-bg-slate-800'
          }`}>
            <div className="tw-space-y-3">
              <p className={`tw-text-sm tw-font-medium ${theme === 'light' ? 'tw-text-gray-800' : 'tw-text-slate-200'}`}>
                Filter by Price Range
              </p>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-relative tw-w-full">
                  <span className={`tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-sm ${theme === 'light' ? 'tw-text-gray-500' : 'tw-text-slate-400'}`}>
                    Rp
                  </span>
                  <input
                    type="number"
                    placeholder="Min"
                    value={priceMin}
                    onChange={(e) => setPriceMin(e.target.value)}
                    className={`tw-w-full tw-rounded-md tw-border tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                      theme === 'light'
                        ? 'tw-border-gray-300 tw-bg-white tw-text-gray-900'
                        : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200'
                    }`}
                  />
                </div>
                <span className={theme === 'light' ? 'tw-text-gray-400' : 'tw-text-slate-500'}>-</span>
                <div className="tw-relative tw-w-full">
                  <span className={`tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-sm ${theme === 'light' ? 'tw-text-gray-500' : 'tw-text-slate-400'}`}>
                    Rp
                  </span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className={`tw-w-full tw-rounded-md tw-border tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                      theme === 'light'
                        ? 'tw-border-gray-300 tw-bg-white tw-text-gray-900'
                        : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200'
                    }`}
                  />
                </div>
              </div>
              <div className="tw-flex tw-justify-end tw-gap-2">
                <button
                  onClick={handlePriceCancel}
                  className={`tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors ${
                    theme === 'light'
                      ? 'tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50'
                      : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200 hover:tw-bg-slate-600'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePriceApply}
                  className="tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 tw-transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sales Range Filter */}
      <div className="tw-relative">
        <button
          onClick={handleSalesButtonClick}
          data-testid="sales-filter"
          className={`tw-flex tw-h-9 tw-shrink-0 tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-4 tw-py-2 tw-transition-colors ${
            activeDropdown === 'sales' || hasSalesFilter
              ? 'tw-border-primary tw-bg-primary/10'
              : 'tw-border-gray-300 tw-bg-white hover:tw-bg-gray-50'
          }`}
        >
          <p className={`tw-text-sm tw-font-medium tw-leading-normal ${
            activeDropdown === 'sales' || hasSalesFilter ? 'tw-text-primary' : 'tw-text-gray-800'
          }`}>
            Sales (30d){formatSalesRange(filters.salesRange)}
          </p>
          <span className={`material-symbols-outlined ${
            activeDropdown === 'sales' || hasSalesFilter ? 'tw-text-primary' : 'tw-text-gray-500'
          }`}>
            expand_more
          </span>
        </button>
        {activeDropdown === 'sales' && (
          <div className={`tw-absolute tw-left-0 tw-mt-2 tw-w-64 tw-origin-top-left tw-rounded-lg tw-border tw-p-4 tw-shadow-lg tw-z-[9999] ${
            theme === 'light' 
              ? 'tw-border-gray-200 tw-bg-white' 
              : 'tw-border-slate-600 tw-bg-slate-800'
          }`}>
            <div className="tw-space-y-3">
              <p className={`tw-text-sm tw-font-medium ${theme === 'light' ? 'tw-text-gray-800' : 'tw-text-slate-200'}`}>
                Filter by Sales Revenue (30 days)
              </p>
              <div className="tw-flex tw-items-center tw-gap-2">
                <div className="tw-relative tw-w-full">
                  <span className={`tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-sm ${theme === 'light' ? 'tw-text-gray-500' : 'tw-text-slate-400'}`}>
                    Rp
                  </span>
                  <input
                    type="number"
                    placeholder="Min"
                    value={salesMin}
                    onChange={(e) => setSalesMin(e.target.value)}
                    className={`tw-w-full tw-rounded-md tw-border tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                      theme === 'light'
                        ? 'tw-border-gray-300 tw-bg-white tw-text-gray-900'
                        : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200'
                    }`}
                  />
                </div>
                <span className={theme === 'light' ? 'tw-text-gray-400' : 'tw-text-slate-500'}>-</span>
                <div className="tw-relative tw-w-full">
                  <span className={`tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-sm ${theme === 'light' ? 'tw-text-gray-500' : 'tw-text-slate-400'}`}>
                    Rp
                  </span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={salesMax}
                    onChange={(e) => setSalesMax(e.target.value)}
                    className={`tw-w-full tw-rounded-md tw-border tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 ${
                      theme === 'light'
                        ? 'tw-border-gray-300 tw-bg-white tw-text-gray-900'
                        : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200'
                    }`}
                  />
                </div>
              </div>
              <div className="tw-flex tw-justify-end tw-gap-2">
                <button
                  onClick={handleSalesCancel}
                  className={`tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-transition-colors ${
                    theme === 'light'
                      ? 'tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50'
                      : 'tw-border-slate-600 tw-bg-slate-700 tw-text-slate-200 hover:tw-bg-slate-600'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSalesApply}
                  className="tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 tw-transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FilterPanel